import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiFetch } from "../services/api";
import {
  User,
  Lead,
  LeadList,
  downloadContacts,
  refreshAccount,
} from "../types";
import ContactDrawer from "../components/ContactDrawer";
export default function MyLeadsPage({ user }: { user: User }) {
  const [lists, setLists] = useState<LeadList[]>([]),
    [active, setActive] = useState(""),
    [leads, setLeads] = useState<Lead[]>([]),
    [selected, setSelected] = useState<number[]>([]),
    [keyword, setKeyword] = useState(""),
    [page, setPage] = useState(1),
    [loading, setLoading] = useState(true),
    [message, setMessage] = useState(""),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false),
    [version, setVersion] = useState(0);
  const [listForm, setListForm] = useState<"new" | "rename" | null>(null),
    [name, setName] = useState(""),
    [drawer, setDrawer] = useState<Lead | null>(null),
    [confirm, setConfirm] = useState<Lead[]>([]);
  useEffect(() => {
    apiFetch("/api/workspace/lists")
      .then(async (r) => {
        if (r.ok) setLists((await r.json()).lists);
      })
      .catch(() => setError("Unable to load lists."));
  }, [version]);
  useEffect(() => {
    setLoading(true);
    setSelected([]);
    setPage(1);
    setError("");
    apiFetch(active ? `/api/workspace/lists/${active}/leads` : "/api/leads/my")
      .then(async (r) => {
        const p = await r.json();
        if (!r.ok) throw new Error(p.error || "Unable to load contacts.");
        setLeads(p.leads);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [active, version]);
  const filtered = leads.filter((l) =>
    [l.first_name, l.last_name, l.company, l.job_title]
      .join(" ")
      .toLowerCase()
      .includes(keyword.toLowerCase()),
  );
  const shown = filtered.slice((page - 1) * 20, page * 20),
    activeList = lists.find((l) => String(l.id) === active);
  const exportLeads = async (all: boolean) => {
    setBusy(true);
    setError("");
    try {
      await downloadContacts(
        all
          ? { all: true, ...(active ? { list_id: Number(active) } : {}) }
          : { lead_ids: selected },
      );
      setMessage("Your CSV download is ready. No credits were deducted.");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Download failed.");
    }
    setBusy(false);
  };
  const saveList = async () => {
    setBusy(true);
    try {
      const r = await apiFetch(
        `/api/workspace/lists${listForm === "rename" ? "/" + active : ""}`,
        {
          method: listForm === "rename" ? "PATCH" : "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name }),
        },
      );
      const p = await r.json();
      if (!r.ok) throw new Error(p.error);
      if (listForm === "new") setActive(String(p.id));
      setVersion((v) => v + 1);
      setListForm(null);
      setMessage("List saved. Add prospects from Find leads.");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unable to save list.");
    }
    setBusy(false);
  };
  const remove = async (id: number) => {
    try {
      const r = await apiFetch(`/api/workspace/lists/${active}/leads/${id}`, {
        method: "DELETE",
      });
      if (!r.ok) throw new Error();
      setVersion((v) => v + 1);
    } catch {
      setError("Unable to remove contact from list.");
    }
  };
  const deleteList = async () => {
    if (
      !window.confirm(
        "Delete this list? Your contacts and unlocks will be kept.",
      )
    )
      return;
    try {
      const r = await apiFetch(`/api/workspace/lists/${active}`, {
        method: "DELETE",
      });
      if (!r.ok) throw new Error();
      setActive("");
      setVersion((v) => v + 1);
    } catch {
      setError("Unable to delete list.");
    }
  };
  const unlock = async () => {
    setBusy(true);
    try {
      const r = await apiFetch("/api/leads/bulk-unlock", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lead_ids: confirm.map((l) => l.id) }),
      });
      const p = await r.json();
      if (!r.ok) throw new Error(p.error);
      setConfirm([]);
      setDrawer(null);
      setVersion((v) => v + 1);
      refreshAccount();
      setMessage("Contacts unlocked.");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unable to unlock contacts.");
    }
    setBusy(false);
  };
  return (
    <>
      <div className="page-title">
        <div>
          <span className="eyebrow">A PLACE FOR YOUR BEST PROSPECTS</span>
          <h1>My lists</h1>
          <p>
            Organize by market, campaign, or client. Your unlocks stay yours.
          </p>
        </div>
        <button
          className="button button-dark"
          onClick={() => {
            setName("");
            setListForm("new");
          }}
        >
          + Create list
        </button>
      </div>
      {message && (
        <p className="notice" role="status">
          {message}
        </p>
      )}
      {error && (
        <p className="notice error" role="alert">
          {error}
        </p>
      )}
      <div className="list-tabs">
        <button
          className={!active ? "active" : ""}
          onClick={() => setActive("")}
        >
          ⌑ All unlocked contacts
        </button>
        {lists.map((l) => (
          <button
            className={active === String(l.id) ? "active" : ""}
            key={l.id}
            onClick={() => setActive(String(l.id))}
          >
            ▤ {l.name} <span>{l.count}</span>
          </button>
        ))}
      </div>
      <section className="panel contacts-panel">
        <div className="panel-heading">
          <div>
            <h2>{activeList?.name || "All unlocked contacts"}</h2>
            <p className="muted">
              {filtered.length.toLocaleString()} contacts
              {active ? " · locked contacts can be unlocked with credits" : ""}
            </p>
          </div>
          <div className="inline-actions">
            {active && (
              <>
                <button
                  className="text-button"
                  onClick={() => {
                    setName(activeList?.name || "");
                    setListForm("rename");
                  }}
                >
                  Rename
                </button>
                <button
                  className="text-button danger"
                  onClick={() => void deleteList()}
                >
                  Delete list
                </button>
              </>
            )}
            <button
              className="button button-outline"
              disabled={busy || !leads.some((l) => l.unlocked)}
              onClick={() => void exportLeads(true)}
            >
              ↓ Export {active ? "list" : "all unlocked"}
            </button>
          </div>
        </div>
        <div className="list-search">
          <input
            aria-label="Search your contacts"
            placeholder="Search your contacts…"
            value={keyword}
            onChange={(e) => {
              setKeyword(e.target.value);
              setPage(1);
            }}
          />
          {selected.length > 0 && (
            <div className="inline-actions">
              <span>{selected.length} selected</span>
              <button
                className="button button-outline"
                disabled={busy}
                onClick={() => void exportLeads(false)}
              >
                Export selected
              </button>
              {filtered.some((l) => selected.includes(l.id) && !l.unlocked) && (
                <button
                  className="button button-dark"
                  onClick={() =>
                    setConfirm(
                      filtered.filter(
                        (l) => selected.includes(l.id) && !l.unlocked,
                      ),
                    )
                  }
                >
                  Unlock selected
                </button>
              )}
            </div>
          )}
        </div>
        {loading ? (
          <div className="skeleton-table">
            {[1, 2, 3].map((i) => (
              <div key={i} />
            ))}
          </div>
        ) : shown.length ? (
          <div className="contact-table-wrap">
            <table className="contact-table">
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      aria-label="Select all visible contacts"
                      checked={
                        shown.length > 0 &&
                        shown.every((l) => selected.includes(l.id))
                      }
                      onChange={(e) =>
                        setSelected(
                          e.target.checked ? shown.map((l) => l.id) : [],
                        )
                      }
                    />
                  </th>
                  <th>CONTACT</th>
                  <th>COMPANY</th>
                  <th>EMAIL</th>
                  <th>PHONE</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {shown.map((l) => (
                  <tr key={l.id}>
                    <td className="select-cell">
                      <input
                        aria-label={`Select ${l.first_name}`}
                        type="checkbox"
                        checked={selected.includes(l.id)}
                        onChange={(e) =>
                          setSelected((old) =>
                            e.target.checked
                              ? [...old, l.id]
                              : old.filter((id) => id !== l.id),
                          )
                        }
                      />
                    </td>
                    <td>
                      <button
                        className="contact-name"
                        onClick={() => setDrawer(l)}
                      >
                        <span className="contact-avatar">
                          {l.first_name[0]}
                          {l.last_name[0]}
                        </span>
                        <span>
                          <strong>
                            {l.first_name} {l.last_name}
                          </strong>
                          <small>{l.job_title || "Business contact"}</small>
                        </span>
                      </button>
                    </td>
                    <td>
                      <strong>{l.company}</strong>
                      <small>{l.industry}</small>
                    </td>
                    <td>
                      {l.email || <span className="locked-chip">Locked</span>}
                    </td>
                    <td>
                      {l.unlocked
                        ? l.phone || "Not provided"
                        : l.phone_available
                          ? "Available · locked"
                          : "Not provided"}
                    </td>
                    <td>
                      <div className="inline-actions">
                        <button
                          className="text-button"
                          onClick={() => setDrawer(l)}
                        >
                          Details ↗
                        </button>
                        {active && (
                          <button
                            className="text-button danger"
                            aria-label={`Remove ${l.first_name} from list`}
                            onClick={() => void remove(l.id)}
                          >
                            ×
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-state">
            <span>▤</span>
            <h2>
              {active
                ? "Your shortlist starts here"
                : "No unlocked contacts yet"}
            </h2>
            <p>
              {active
                ? "Select contacts in Find leads and add them to this list."
                : "Find a contact, preview the details, and use a credit to unlock it."}
            </p>
            <Link className="button button-dark" to="/search">
              Find leads ↗
            </Link>
          </div>
        )}
        <div className="pagination">
          <span>
            Page {page} of {Math.max(1, Math.ceil(filtered.length / 20))}
          </span>
          <div>
            <button
              className="button button-outline"
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
            >
              ← Previous
            </button>
            <button
              className="button button-outline"
              disabled={page * 20 >= filtered.length}
              onClick={() => setPage((p) => p + 1)}
            >
              Next →
            </button>
          </div>
        </div>
      </section>
      {drawer && (
        <ContactDrawer
          lead={drawer}
          onClose={() => setDrawer(null)}
          onUnlock={(l) => setConfirm([l])}
        />
      )}{" "}
      {listForm && (
        <div className="modal-overlay">
          <form
            className="modal-card"
            role="dialog"
            aria-modal="true"
            aria-labelledby="list-title"
            onSubmit={(e) => {
              e.preventDefault();
              void saveList();
            }}
          >
            <h2 id="list-title">
              {listForm === "new" ? "Create a list" : "Rename list"}
            </h2>
            <label htmlFor="list-name">List name</label>
            <input
              id="list-name"
              autoFocus
              required
              maxLength={80}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Austin founders"
            />
            <div className="modal-actions">
              <button
                type="button"
                className="button button-outline"
                onClick={() => setListForm(null)}
              >
                Cancel
              </button>
              <button className="button button-dark" disabled={busy}>
                Save list
              </button>
            </div>
          </form>
        </div>
      )}
      {confirm.length > 0 && (
        <div className="modal-overlay">
          <section
            className="modal-card"
            role="dialog"
            aria-modal="true"
            aria-labelledby="list-unlock-title"
          >
            <h2 id="list-unlock-title">
              Unlock {confirm.length} contact{confirm.length === 1 ? "" : "s"}?
            </h2>
            <p>
              This will use {confirm.length} credits. Your available balance:{" "}
              {user.credits.toLocaleString()}.
            </p>
            <div className="modal-actions">
              <button
                className="button button-outline"
                onClick={() => setConfirm([])}
              >
                Cancel
              </button>
              <button
                className="button button-dark"
                disabled={
                  busy ||
                  user.subscription_status !== "active" ||
                  user.credits < confirm.length
                }
                onClick={() => void unlock()}
              >
                Confirm unlock
              </button>
            </div>
            {user.subscription_status !== "active" && (
              <Link to="/pricing">Choose a plan to unlock contacts →</Link>
            )}
          </section>
        </div>
      )}
    </>
  );
}
